import React, { useState } from "react";
import {
  FiX,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiMail,
  FiUser,
  FiCheck,
  FiAlertCircle,
  FiLoader,
} from "react-icons/fi";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

// ─── Event Details (update when confirmed) ────────────────────────────────────
const EVENT = {
  date: "Saturday 28th March, 2026",
  time: "4:00 PM WAT",
  venue: "Abuja, Nigeria (exact location will be shared with attendees)",
  contact: "madeformorewithavese@gmail.com",
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const EventDetailRow = ({ Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-purple-50 last:border-0">
    <div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-purple-50 flex items-center justify-center">
      <Icon size={13} className="text-purple-600" />
    </div>
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-purple-400 mb-0.5">
        {label}
      </p>
      <p className="text-sm text-gray-800 font-medium leading-snug">{value}</p>
    </div>
  </div>
);

const InputField = ({
  id,
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  disabled,
}) => (
  <div className="flex flex-col gap-1">
    <label
      htmlFor={id}
      className="text-xs font-semibold uppercase tracking-widest text-gray-500"
    >
      {label}
    </label>
    <input
      id={id}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      autoComplete="off"
      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-800 text-sm placeholder-gray-300
                 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent
                 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
    />
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const RegistrationModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const [flipped, setFlipped] = useState(false);

  if (!isOpen) return null;

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClose = () => {
    // Reset state on close
    setForm({ firstName: "", lastName: "", email: "" });
    setStatus("idle");
    setErrorMessage("");
    setFlipped(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const res = await fetch(`${API_BASE}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus("success");
        setFlipped(true);
      } else {
        setStatus("error");
        setErrorMessage(
          data.message || "Something went wrong. Please try again.",
        );
        setFlipped(true);
      }
    } catch {
      setStatus("error");
      setErrorMessage(
        "Unable to reach the server. Please check your connection.",
      );
      setFlipped(true);
    }
  };

  return (
    /* ── Backdrop ── */
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{
        background: "rgba(10, 6, 20, 0.75)",
        backdropFilter: "blur(6px)",
      }}
      onClick={handleClose}
    >
      {/* ── Flip card wrapper ── */}
      <div
        className="relative w-full max-w-lg my-auto"
        style={{ perspective: "1200px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            transformStyle: "preserve-3d",
            transition: "transform 0.65s cubic-bezier(0.4, 0.2, 0.2, 1)",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
            position: "relative",
            minHeight: "560px",
            maxHeight: "calc(100vh - 2rem)",
          }}
        >
          {/* ════════════════════════════════════════════
              FRONT — Registration Form
          ════════════════════════════════════════════ */}
          <div
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
            className="absolute inset-0 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
              aria-label="Close modal"
            >
              <FiX size={15} className="text-gray-600" />
            </button>

            {/* Header strip */}
            <div
              className="px-7 pt-8 pb-6"
              style={{
                background:
                  "linear-gradient(135deg, #7c3aed 0%, #9f24f0 55%, #c026d3 100%)",
              }}
            >
              <p className="text-[10px] font-semibold tracking-[3px] uppercase text-purple-200 mb-1">
                Book Launch Event
              </p>
              <h2 className="text-2xl font-bold text-white leading-tight">
                The More Experience
              </h2>
              <p className="text-sm text-purple-100 mt-1 leading-relaxed">
                Secure your seat at an encounter that will redefine your
                capacity.
              </p>
            </div>

            <div className="px-7 py-5 flex-1 overflow-y-auto hide-scrollbar">
              {/* Form */}
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-4 mb-8 mt-2"
              >
                <p className="text-[10px] font-bold uppercase tracking-[3px] text-gray-400 -mb-1">
                  Registration Form
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    id="reg-firstName"
                    label="First Name"
                    placeholder="Ada"
                    value={form.firstName}
                    onChange={handleChange("firstName")}
                    disabled={status === "loading"}
                  />
                  <InputField
                    id="reg-lastName"
                    label="Last Name"
                    placeholder="Okonkwo"
                    value={form.lastName}
                    onChange={handleChange("lastName")}
                    disabled={status === "loading"}
                  />
                </div>

                <InputField
                  id="reg-email"
                  label="Email Address"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={handleChange("email")}
                  disabled={status === "loading"}
                />

                <button
                  type="submit"
                  disabled={
                    status === "loading" ||
                    !form.firstName ||
                    !form.lastName ||
                    !form.email
                  }
                  className="mt-1 w-full py-3 rounded-xl text-white text-sm font-semibold tracking-wide 
                             flex items-center justify-center gap-2 transition-all duration-200
                             disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "linear-gradient(135deg, #7c3aed 0%, #9f24f0 55%, #c026d3 100%)",
                    boxShadow: "0 4px 20px rgba(124, 58, 237, 0.35)",
                  }}
                >
                  {status === "loading" ? (
                    <>
                      <FiLoader size={15} className="animate-spin" />
                      Registering…
                    </>
                  ) : (
                    <>
                      <FiUser size={15} />
                      Register Now
                    </>
                  )}
                </button>

                <p className="text-center text-[11px] text-gray-400 -mt-1">
                  A confirmation email will be sent upon successful
                  registration.
                </p>
              </form>

              {/* Event Details */}
              <div className="mb-5 bg-purple-50 rounded-xl px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-[3px] text-purple-500 mb-2">
                  Event Details
                </p>
                <EventDetailRow
                  Icon={FiCalendar}
                  label="Date"
                  value={EVENT.date}
                />
                <EventDetailRow
                  Icon={FiClock}
                  label="Time"
                  value={EVENT.time}
                />
                <EventDetailRow
                  Icon={FiMapPin}
                  label="Venue"
                  value={EVENT.venue}
                />
                <EventDetailRow
                  Icon={FiMail}
                  label="Contact"
                  value={EVENT.contact}
                />
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════
              BACK — Success / Error State
          ════════════════════════════════════════════ */}
          <div
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
            className="absolute inset-0 bg-white rounded-2xl shadow-2xl flex flex-col items-center justify-center px-8 py-10 overflow-y-auto hide-scrollbar"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
              aria-label="Close modal"
            >
              <FiX size={15} className="text-gray-600" />
            </button>

            {status === "success" ? (
              /* ── Success State ── */
              <div className="flex flex-col items-center text-center gap-5">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    boxShadow: "0 8px 32px rgba(16, 185, 129, 0.35)",
                  }}
                >
                  <FiCheck size={36} strokeWidth={3} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 tracking-tight">
                    You're registered!
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 leading-relaxed max-w-xs">
                    Welcome,{" "}
                    <strong className="text-gray-700">{form.firstName}</strong>!
                    We've sent a confirmation to{" "}
                    <strong className="text-gray-700">{form.email}</strong>.
                  </p>
                </div>
                <div className="w-full bg-green-50 border border-green-100 rounded-xl px-5 py-4 text-sm text-green-700 leading-relaxed">
                  🎉 We can't wait to see you at{" "}
                  <strong>The More Experience</strong>.<br />
                  Check your inbox for full event details.
                </div>
                <button
                  onClick={handleClose}
                  className="mt-2 px-8 py-2.5 rounded-xl text-white text-sm font-semibold transition-all duration-200"
                  style={{
                    background: "linear-gradient(135deg, #10b981, #059669)",
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              /* ── Error State ── */
              <div className="flex flex-col items-center text-center gap-5">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    boxShadow: "0 8px 32px rgba(239, 68, 68, 0.35)",
                  }}
                >
                  <FiAlertCircle
                    size={36}
                    strokeWidth={2.5}
                    className="text-white"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 tracking-tight">
                    Registration Failed
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 leading-relaxed max-w-xs">
                    {errorMessage}
                  </p>
                </div>
                <div className="w-full bg-red-50 border border-red-100 rounded-xl px-5 py-4 text-sm text-red-600 leading-relaxed">
                  If the problem persists, email us directly at{" "}
                  <a
                    href="mailto:madeformorewithavese@gmail.com"
                    className="font-semibold underline"
                  >
                    madeformorewithavese@gmail.com
                  </a>
                </div>
                <div className="flex gap-3 mt-1">
                  <button
                    onClick={() => {
                      setFlipped(false);
                      setStatus("idle");
                      setErrorMessage("");
                    }}
                    className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors duration-200"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={handleClose}
                    className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold transition-all duration-200"
                    style={{
                      background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationModal;
